/**
 * @file Network monitoring utility for detecting online/offline status
 * Based on VueUse useNetwork but without Vue reactivity
 */

const NETWORK_CHECK_INTERVAL_MS = 5 * 1000

export type NetworkType = 'bluetooth' | 'cellular' | 'ethernet' | 'none' | 'wifi' | 'wimax' | 'other' | 'unknown'
export type NetworkEffectiveType = 'slow-2g' | '2g' | '3g' | '4g' | undefined

export interface NetworkState {
  isOnline: boolean
  offlineAt?: number
  onlineAt?: number
  isSupported: boolean
  downlink?: number
  downlinkMax?: number
  effectiveType?: NetworkEffectiveType
  rtt?: number
  saveData?: boolean
  type?: NetworkType
}

export class NetworkMonitor {
  private state: NetworkState = {
    isOnline: true,
    isSupported: false,
    offlineAt: undefined,
    onlineAt: undefined,
    downlink: undefined,
    downlinkMax: undefined,
    effectiveType: undefined,
    rtt: undefined,
    saveData: undefined,
    type: 'unknown',
  }

  constructor() {
    this.state.isSupported = this.checkSupport()
    if (this.state.isSupported && typeof window !== 'undefined') {
      this.state.isOnline = window.navigator.onLine
    }
    this.updateConnectionInformation()
    this.setupEventListeners()
  }

  private checkSupport(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.navigator !== 'undefined' &&
      typeof window.navigator.onLine !== 'undefined'
    )
  }

  private updateConnectionInformation(): void {
    if (!this.state.isSupported || typeof window === 'undefined') return

    // Update Network Information API properties if available
    const connection =
      (window.navigator as any).connection ||
      (window.navigator as any).mozConnection ||
      (window.navigator as any).webkitConnection

    if (connection) {
      this.state.downlink = connection.downlink
      this.state.downlinkMax = connection.downlinkMax
      this.state.effectiveType = connection.effectiveType as NetworkEffectiveType
      this.state.rtt = connection.rtt
      this.state.saveData = connection.saveData
      this.state.type = (connection.type || 'unknown') as NetworkType
    }
  }

  private setupEventListeners(): void {
    if (!this.state.isSupported || typeof window === 'undefined') return

    const options = { passive: true }

    window.addEventListener(
      'offline',
      () => {
        this.state.isOnline = false
        this.state.offlineAt = Date.now()
        this.state.onlineAt = undefined
        this.updateConnectionInformation()
      },
      options,
    )

    window.addEventListener(
      'online',
      () => {
        this.state.isOnline = true
        this.state.onlineAt = Date.now()
        this.state.offlineAt = undefined
        this.updateConnectionInformation()
      },
      options,
    )

    const connection =
      (window.navigator as any).connection ||
      (window.navigator as any).mozConnection ||
      (window.navigator as any).webkitConnection

    if (connection && connection.addEventListener) {
      connection.addEventListener(
        'change',
        () => {
          this.updateConnectionInformation()
        },
        options,
      )
    }
  }

  getState(): NetworkState {
    return { ...this.state }
  }

  isOnline(): boolean {
    return this.state.isOnline
  }

  getDownlink(): number | undefined {
    return this.state.downlink
  }

  getDownlinkMax(): number | undefined {
    return this.state.downlinkMax
  }

  getEffectiveType(): NetworkEffectiveType {
    return this.state.effectiveType
  }

  getRtt(): number | undefined {
    return this.state.rtt
  }

  isSaveDataEnabled(): boolean | undefined {
    return this.state.saveData
  }

  getConnectionType(): NetworkType {
    return this.state.type || 'unknown'
  }

  /**
   * Waits for network to come back online
   * @param {AbortController} abortController - Controller to abort waiting if upload is cancelled
   * @returns {Promise<{cleanup: () => void}>} Resolves when network is back online or upload is cancelled, returns cleanup function
   */
  async waitForNetworkRecovery(abortController: AbortController): Promise<{ cleanup: () => void }> {
    return new Promise<{ cleanup: () => void }>((resolve) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null
      let onlineHandler: (() => void) | null = null

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
        if (onlineHandler && typeof window !== 'undefined') {
          window.removeEventListener('online', onlineHandler)
          onlineHandler = null
        }
      }

      const resolveWithCleanup = () => {
        resolve({ cleanup })
      }

      const checkNetwork = () => {
        if (abortController.signal.aborted) {
          resolveWithCleanup() // Upload was cancelled, exit
          return
        }

        if (this.isOnline()) {
          resolveWithCleanup() // Network is back, continue upload
          return
        }

        // Still offline, check again after interval
        timeoutId = setTimeout(checkNetwork, NETWORK_CHECK_INTERVAL_MS)
      }

      // Also listen for online event if available
      if (typeof window !== 'undefined' && window.addEventListener) {
        onlineHandler = resolveWithCleanup
        window.addEventListener('online', onlineHandler)
      }

      checkNetwork()
    })
  }
}

// Global network monitor instance
export const networkMonitor = new NetworkMonitor()
